import type {
  EdgeTreatment,
  LayerTransform,
  LedSpec,
  PanelDesign,
  PanelGeometrySpec,
  ParamValues,
  PatternLayer,
  ReliefFinish,
} from '../types';
import { defaultParamValues } from '../params';
import { requirePattern } from '../patterns/registry';

/** Factory functions for every part of a design. */

export const DESIGN_VERSION = 1;

let layerCounter = 0;

/** Stable, collision-free layer id that also works outside a browser. */
export function createId(prefix = 'layer'): string {
  layerCounter += 1;
  const random = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${layerCounter.toString(36)}-${random}`;
}

export function identityTransform(): LayerTransform {
  return { offsetX: 0, offsetY: 0, rotation: 0, scale: 1, mirrorX: false, mirrorY: false };
}

export function createLayer(
  patternId: string,
  overrides: Partial<Omit<PatternLayer, 'patternId'>> = {},
): PatternLayer {
  const definition = requirePattern(patternId);
  return {
    id: overrides.id ?? createId(),
    patternId,
    params: { ...defaultParamValues(definition.params), ...(overrides.params ?? {}) },
    enabled: overrides.enabled ?? true,
    amount: overrides.amount ?? 1,
    blend: overrides.blend ?? 'replace',
    transform: { ...identityTransform(), ...(overrides.transform ?? {}) },
  };
}

/** Params for a pattern, merged over its declared defaults. */
export function paramsFor(patternId: string, overrides: ParamValues = {}): ParamValues {
  return { ...defaultParamValues(requirePattern(patternId).params), ...overrides };
}

export function defaultEdgeTreatment(): EdgeTreatment {
  return { mode: 'seamless', width: 4 };
}

export function defaultFinish(): ReliefFinish {
  return { gamma: 1, contrast: 0, terraceSteps: 0, invert: false, autoLevel: true };
}

/**
 * Backlighting defaults.
 *
 * Chosen so that switching backlighting on produces an evenly lit wall rather
 * than a warning. Defaults that fail their own checks teach people to ignore
 * the checks.
 *
 * Getting there is more demanding than it looks: even illumination wants runs
 * spaced no wider than about 1.5 times the air gap, so a 150 mm panel needs
 * four runs, not one. That is a lot of strip, and it is what an evenly backlit
 * panel actually costs. Someone who would rather have bright bands can widen
 * the spacing and the check will tell them what they are getting instead.
 */
export function defaultLedSpec(): LedSpec {
  return {
    enabled: false,
    // A groove in the panel is the simpler wall: one part per tile, hanging
    // flat. The light box buys an even glow at the cost of a second print, so
    // it is the deliberate choice rather than the one you get by default.
    mounting: 'channel',
    // The seat for the tape itself. The pocket the emitters stand in is added
    // below it from the strip's own package height, so this is not the whole
    // groove — a 60 LEDs/m strip puts another 1.6 mm under this.
    channelDepth: 1.2,
    airGap: 30,
    ledType: 'd60',
    panelMaterial: 'white',
    frameWall: 2.4,
    frameFloor: 1.6,
    layout: 'horizontal',
    stripWidth: 10,
    spacing: 0.25,
    offset: 0.5,
    inset: 20,
    passThrough: true,
  };
}

export function defaultPanelSpec(): PanelGeometrySpec {
  return {
    shapeId: 'square',
    size: 150,
    gap: 0,
    baseThickness: 2,
    reliefDepth: 8,
    segments: 96,
    edge: defaultEdgeTreatment(),
  };
}

export function createDefaultDesign(): PanelDesign {
  return {
    version: DESIGN_VERSION,
    name: 'Untitled wall',
    grid: { columns: 3, rows: 2 },
    panel: defaultPanelSpec(),
    source: {
      kind: 'pack',
      label: 'Soft Waves',
      presetId: 'soft-waves',
      layers: [createLayer('waves', { params: { wavelength: 1, profile: 'sine' } })],
    },
    finish: defaultFinish(),
    led: defaultLedSpec(),
    assets: [],
  };
}
